import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { validate as isUUID } from 'uuid';

import { EMPTY_DROPDOWN_OPTION } from '~/constants/select';

import {
  type CostCenter,
  fetchCostCenter,
  fetchCostCenters,
  queryKeysCostCenter,
} from '~/data/costCenter';
import { useGetUserUtils } from '~/data/user';

import { OptionPrimarySecondary } from '@vestigas/ui/molecules';

import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ name }: CostCenter) => name;

type MultiSelectCostCentersProps = {
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (option: Option<CostCenter>) => React.ReactNode;
  readonly hasEmptyOption?: boolean;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<CostCenter>) => boolean;
  readonly onChange: (
    value: Array<{ id: string }> | { id: string },
    data: CostCenter[] | CostCenter,
  ) => void;
  readonly placeholder?: string;
  readonly value: string | string[];
} & ComponentStyling;

/**
 * Formats select options like
 * ----------------------
 * <costCenter>
 * <companyName>
 * ----------------------
 */
export const costCenterCompanyNameOptionLabel = ({
  data,
}: Option<CostCenter>) => {
  const companyName =
    'companyName' in data && typeof data.companyName === 'string'
      ? data.companyName
      : undefined;
  return (
    <OptionPrimarySecondary
      primaryInformation={data.name}
      secondaryInformation={companyName}
    />
  );
};

export const MultiSelectCostCenters = ({
  className,
  filterActive = true,
  formatOptionLabel = costCenterCompanyNameOptionLabel,
  hasEmptyOption = false,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  placeholder = 'Kostenstellen',
  onChange,
  style,
  value,
}: MultiSelectCostCentersProps) => {
  const queryClient = useQueryClient();

  const { UserUtils } = useGetUserUtils();

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<CostCenter>({
    createQueryKey: ({ page, ...queryParams }) =>
      queryKeysCostCenter.getAll(queryParams),
    async fetchItems({ page, ...queryParams }) {
      const { data, hasNextPage } = await fetchCostCenters(queryParams);

      if (hasEmptyOption) {
        return {
          data: [
            {
              id: EMPTY_DROPDOWN_OPTION,
              name: EMPTY_DROPDOWN_OPTION,
            },
            ...data,
          ],
          hasNextPage,
        };
      }

      return {
        data,
        hasNextPage,
      };
    },
    filterActive,
    getOptionLabel,
    queryClient,
  });

  /**
   * Gets costCenter data for a costCenter with a given costCenterId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the costCenter data is not in the cache.
   * @param {string} costCenterId - The ID of the costCenter to get.
   */
  const getItemData = createGetItemData<CostCenter>({
    createQueryKey: (costCenterId) => queryKeysCostCenter.get(costCenterId),
    async fetchItem(id) {
      if (hasEmptyOption && id === EMPTY_DROPDOWN_OPTION) {
        return {
          id: EMPTY_DROPDOWN_OPTION,
          name: EMPTY_DROPDOWN_OPTION,
        };
      }

      // Stale persisted filters may hold cost center names instead of ids
      // (DNI-848). Skip the request for non-UUID values; fall back to
      // rendering the raw value so the user can remove it.
      if (!isUUID(id)) {
        return { id, name: id };
      }

      return fetchCostCenter(id);
    },
    queryClient,
    queryKeyBase: [queryKeysCostCenter.base()],
  });

  // This is just a glue function b/c PermissionGrantMultiPicker currently expects to pick the ID from an object in its onChange function. Should be refactored.
  const handleChange = useCallback(
    (newValues: string[] | string, data: CostCenter[] | CostCenter) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues },
        data,
      );
    },
    [onChange],
  );

  return (
    <SelectServerDriven<CostCenter>
      className={className}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={UserUtils.isCostCenterReadAllowedUser}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
